/**
 * 送出／重送／送達確認：說、私訊、重送、用第二個帳號確認，以及「帳號已死」的統一處理。
 *
 * 職責邊界：**只**碰「一則訊息從鍵盤到站方」這條路徑。開通身分、批次、房間都不在這裡。
 * 行為與拆檔前一致（2026-10-27 T10）：樂觀更新、5 秒歷史對帳、UT 不回播不排對帳。
 */
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  HISTORY_VERIFY_DELAY_MS,
  isInvalidSessionError,
  peerOutcomeMessage,
  shouldVerifyByHistory,
} from '@chat-rea/console-ui';
import type {
  Conversation,
  InboxEvent,
  NoticeKind,
  UseInboxResult,
} from '@chat-rea/console-ui';
import { client } from '../api';
import { INVALID_SESSION_NOTICE } from './consoleActions.shared';

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * 會傳進 memo 子元件的回呼（say／sendPm／retryOutgoing）不能在 deps 裡放 `conversations`／
 * `activeEvents`／`active` 這些「每次訊息進來就換身分」的值：
 * undefined `active` 一換，`say` 就換，`retryOutgoing` 跟著換，`ThreadRow` 的 memo 對整個
 * 對話串（實測 34～170 列）全部失效——每次 WS 訊息、每次送出都重算整串，這是 80–160ms
 * 長工作的來源。改成呼叫當下從 ref 讀最新值，回呼本身保持同一個參考。
 */
export interface LiveRefValue {
  active: Conversation | null;
  activeEvents: UseInboxResult['activeEvents'];
  conversations: Conversation[];
}

export interface SendActionsDeps {
  inbox: UseInboxResult;
  liveRef: { current: LiveRefValue };
  targetRoom: string;
  setBusy: Dispatch<SetStateAction<boolean>>;
  setActionError: (message: string | null) => void;
  setNotice: (message: string | null, kind?: NoticeKind) => void;
  clearErrorNotices: (kind: NoticeKind) => void;
}

export interface SendActions {
  busyRun: (action: () => Promise<void>) => Promise<void>;
  /**
   * 後端說「這個 session 已經不存在／已休眠」→ 把對話從清單移除，並改講一句人話。
   * 回傳 true 代表已處理（呼叫端不用再拋原始錯誤）。這是「對著死帳號一直按送出」的根本修正：
   * 舊版只把紅字丟在上方，清單還留著那一列，使用者只會一直重按。
   */
  markGone: (err: unknown, id: string) => boolean;
  say: (text: string) => Promise<void>;
  sendPm: (target: string, text: string) => Promise<void>;
  retryOutgoing: (event: InboxEvent) => Promise<void>;
  verifyDelivery: (event: InboxEvent) => Promise<void>;
}

export function useSendActions({
  inbox,
  liveRef,
  targetRoom,
  setBusy,
  setActionError,
  setNotice,
  clearErrorNotices,
}: SendActionsDeps): SendActions {
  const {
    markInvalid,
    recordOutgoing,
    markOutgoingDelivered,
    failOutgoing,
    discardOutgoing,
    refreshEvents,
    verifyPeer,
    refresh,
  } = inbox;

  /** 送出後 5 秒的歷史對帳計時器（一顆身分最多一個；切走／卸載要收）。 */
  const verifyTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // 送出後的歷史對帳計時器：卸載就收乾淨，不要在已卸載的 hook 上發請求。
  useEffect(
    () => () => {
      for (const timer of verifyTimersRef.current.values()) clearTimeout(timer);
      verifyTimersRef.current.clear();
    },
    [],
  );

  /** 所有會打後端的動作共用：busy 開關＋錯誤進橫幅，並把錯誤往上丟給呼叫端顯示。 */
  const busyRun = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true);
      try {
        await action();
        // 重試成功＝舊錯誤結束：清掉 error 這一類，其他通知不動。
        clearErrorNotices('error');
      } catch (err) {
        const message = errMessage(err);
        setActionError(message);
        throw err instanceof Error ? err : new Error(message);
      } finally {
        setBusy(false);
      }
    },
    [clearErrorNotices, setActionError, setBusy],
  );

  const markGone = useCallback(
    (err: unknown, id: string): boolean => {
      if (!isInvalidSessionError(err)) return false;
      markInvalid(id, errMessage(err));
      setActionError(INVALID_SESSION_NOTICE);
      setNotice(null);
      return true;
    },
    [markInvalid, setActionError, setNotice],
  );

  /**
   * 送出前先確認連線：離線（含 DO 休眠）就先打一次 join 喚醒，不要在死帳號上直接送。
   * join 失敗＝真的叫不醒，交給呼叫端判斷是不是「已失效」。
   */
  const ensureOnline = useCallback(
    async (conversation: Conversation) => {
      if (conversation.online) return;
      setNotice('重新連線中…（這個身分目前離線，先重連再送出）');
      await client.joinRoom(conversation.id, conversation.room ?? targetRoom);
      await refresh();
    },
    [refresh, setNotice, targetRoom],
  );

  /** 一般訊息：以目前對話的身分送出。 */
  const say = useCallback(
    async (text: string) => {
      const active = liveRef.current.active;
      if (!active) throw new Error('先在左邊選一個對話，才知道用誰的身分送');
      const id = active.id;
      // 樂觀更新：POST 之前就畫上去（灰色「傳送中…」），聊手不必等站方回播（實測 7 秒）。
      // `service` 決定到期後要不要標「站方無回執」（UT 不會回播自己，見 delivery.ts）。
      const localId = recordOutgoing({
        accountId: id,
        type: 'say',
        from: active.nickname,
        text,
        service: active.service,
      });
      try {
        await busyRun(async () => {
          await ensureOnline(active);
          await client.say(id, text);
        });
        markOutgoingDelivered(localId);
        setNotice(null);
        // 回播到達（或已送到歷史）就取代本地那則，不會兩則；順便對帳未讀。
        void refreshEvents(id);
        // 驗證層（不改送出路徑）：ek21 回播實測 7–10 秒，送出當下抓的歷史常常還沒有自己那句。
        // 5 秒後再抓一次；若回播已在歷史裡，UI 會把「已送出（尚未看到回播）」換成「已確認送達」。
        // UT 不回播自己，多抓一次也證明不了，故不排。
        if (shouldVerifyByHistory(active.service, 'say')) {
          const prior = verifyTimersRef.current.get(id);
          if (prior) clearTimeout(prior);
          verifyTimersRef.current.set(
            id,
            setTimeout(() => {
              verifyTimersRef.current.delete(id);
              void refreshEvents(id);
            }, HISTORY_VERIFY_DELAY_MS),
          );
        }
      } catch (err) {
        // 帳號死了：那一則留著沒有意義（清單已移除），直接拿掉；其他錯誤標成「送出失敗」可重送。
        if (markGone(err, id)) {
          discardOutgoing(localId);
          throw new Error(INVALID_SESSION_NOTICE);
        }
        failOutgoing(localId, errMessage(err));
        throw err;
      }
    },
    [
      busyRun,
      discardOutgoing,
      ensureOnline,
      failOutgoing,
      liveRef,
      markGone,
      markOutgoingDelivered,
      recordOutgoing,
      refreshEvents,
      setNotice,
    ],
  );

  /**
   * 私訊：站方對「剛進房不到 2–3 秒」的對象會靜默丟掉（API 還是回 ok）。
   * 所以先在目前對話的事件裡確認真的看過這個人，再把「可能被丟掉」講清楚。
   */
  const sendPm = useCallback(
    async (target: string, text: string) => {
      const { active, activeEvents, conversations } = liveRef.current;
      if (!active) throw new Error('先在左邊選一個對話，才知道用誰的身分送');
      const id = active.id;
      const name = target.trim();
      const known = new Set<string>([
        ...conversations.map((item) => item.nickname),
        ...activeEvents.map((event) => event.from),
      ]);
      if (!known.has(name)) {
        throw new Error(`對話裡沒看到「${name}」；私訊要和對方在同一間房、且對方已進房才送得到`);
      }
      // 樂觀更新：先畫（標了 to），站方回播到達時取代本地那則。
      // UT 的私訊同樣沒有回播（DELIVERY §4.2-5），所以到期要標「無回執」。
      const localId = recordOutgoing({
        accountId: id,
        type: 'pm',
        from: active.nickname,
        text,
        to: name,
        service: active.service,
      });
      try {
        await busyRun(async () => {
          await ensureOnline(active);
          await client.pm(id, name, text);
        });
        markOutgoingDelivered(localId);
        setNotice(
          `已送出私訊給 ${name}。站方會靜默丟掉剛進房對象的私訊；若對方沒回音，等 2–3 秒再送一次。`,
          'success',
        );
        void refreshEvents(id);
      } catch (err) {
        if (markGone(err, id)) {
          discardOutgoing(localId);
          throw new Error(INVALID_SESSION_NOTICE);
        }
        failOutgoing(localId, errMessage(err));
        throw err;
      }
    },
    [
      busyRun,
      discardOutgoing,
      ensureOnline,
      failOutgoing,
      liveRef,
      markGone,
      markOutgoingDelivered,
      recordOutgoing,
      refreshEvents,
      setNotice,
    ],
  );

  /**
   * 重送一則送失敗的訊息：先拿掉失敗那則再走原本的送出流程（會長出新的「傳送中」那則）。
   * 走 `say`／`sendPm` 而不是直接打 client，是為了保留「先重連、失敗轉人話」那些判斷。
   */
  const retryOutgoing = useCallback(
    async (event: InboxEvent) => {
      discardOutgoing(event.id);
      if (event.type === 'pm' && event.to) {
        await sendPm(event.to, event.text);
        return;
      }
      await say(event.text);
    },
    [discardOutgoing, say, sendPm],
  );

  /**
   * 「用第二個帳號確認」：叫同房（或收件人）的另一顆身分讀訊息列表，看到就證明送達。
   *
   * 只讀不寫：不重送、不改任何 session 狀態。結果用 `notice` 講一句人話
   * （`peerOutcomeMessage` 與 hook 共用同一份措辭，避免「按鈕說一套、標籤說另一套」）。
   */
  const verifyDelivery = useCallback(
    async (event: InboxEvent) => {
      setActionError(null);
      setNotice('正在用第二個帳號確認…');
      try {
        const outcome = await verifyPeer(event.id);
        if (!outcome) {
          setNotice('這則的送出紀錄已不在（重整後不保留），請直接看對方是否回訊。');
          return;
        }
        setNotice(peerOutcomeMessage(outcome));
      } catch (err) {
        setNotice(null);
        setActionError(errMessage(err));
      }
    },
    [setActionError, setNotice, verifyPeer],
  );

  return { busyRun, markGone, say, sendPm, retryOutgoing, verifyDelivery };
}
